import { parse } from 'graphql'
import { useState } from 'react'

import { client } from '~publish/legacy/apollo-client'

const GENERATE_ALT_TEXT_MUTATION = parse(`
  mutation generateAltText(
    $organizationId: String!
    $config: GenerateAltTextInput!
  ) {
    generateAltText(organizationId: $organizationId, config: $config) {
      ... on TextGenerated {
        suggestions {
          content
        }
      }
      ... on CommonError {
        code
        success
        message
        error
      }
    }
  }
`)

type GenerateAltTextData = {
  generateAltText:
    | { suggestions?: { content: string }[] }
    | { code?: string; message?: string }
}

type GenerateAltTextVariables = {
  organizationId: string
  config: { imageUrl: string; locale?: string }
}

const GENERIC_ERROR =
  'Whoops! We could not generate a description right now. Please try again.'

/**
 * Maps known backend error codes (from `@buffer-mono/ai-modules` errors) to tailored,
 * actionable copy. Kept on the frontend rather than surfacing raw backend strings so
 * messaging stays controlled; unknown codes fall back to {@link GENERIC_ERROR}.
 */
const ERROR_MESSAGE_BY_CODE: Record<string, string> = {
  ImageError:
    'We could not read this image. Please try a different image, or write the description manually.',
  InsuffiecientTokens: "Whoops! Looks like you don't have any AI tokens left.",
  Timeout: 'This took longer than expected. Please try again.',
}

type UseGenerateAltTextResult = {
  /**
   * Generates alt text for the image at `imageUrl`. Resolves with the suggested
   * description, or rejects with a user-facing error message.
   */
  generateAltText: (imageUrl: string) => Promise<string>
  isGenerating: boolean
}

/**
 * Calls the `generateAltText` content-generation mutation to produce an AI image
 * description. Reusable across composer surfaces (post composer, ideas) — the caller
 * provides the image URL and decides how to surface the result and any error.
 */
export function useGenerateAltText(
  organizationId: string | null,
): UseGenerateAltTextResult {
  const [isGenerating, setIsGenerating] = useState(false)

  const generateAltText = async (imageUrl: string): Promise<string> => {
    if (!organizationId) {
      throw new Error(GENERIC_ERROR)
    }

    setIsGenerating(true)
    try {
      let response
      try {
        response = await client.mutate<
          GenerateAltTextData,
          GenerateAltTextVariables
        >({
          mutation: GENERATE_ALT_TEXT_MUTATION,
          variables: {
            organizationId,
            // Match the description to the user's browser language; the backend
            // falls back to English when this is absent or unrecognized.
            config: { imageUrl, locale: navigator.language || undefined },
          },
        })
      } catch {
        // Network / transport failures: never surface raw Apollo messages to the user.
        throw new Error(GENERIC_ERROR)
      }

      const result = response.data?.generateAltText

      if (result && 'suggestions' in result && result.suggestions?.[0]) {
        return result.suggestions[0].content
      }

      const code = result && 'code' in result ? result.code : undefined
      throw new Error((code && ERROR_MESSAGE_BY_CODE[code]) || GENERIC_ERROR)
    } finally {
      setIsGenerating(false)
    }
  }

  return { generateAltText, isGenerating }
}
